import './WhoLivesQuestionScreen.css'
import { DefaultScreenProps } from '../defaultScreenProps'
import { useEffect, useRef, useState } from 'react'
import { Victim } from '../../types/victimTypes'
import { isTvMode } from '../../utils/tvMode'
import { COPY } from '../../constants/copy'

type Side = 'left' | 'right'

/**
 * A stray OK/click carried over from skipping the previous screen must not
 * sentence anyone before the player has even seen the pair.
 */
const CHOICE_GRACE_MS = 500

type WhoLivesQuestionScreenProps = DefaultScreenProps & {
  sayVictimIntro: () => void
  promptWhoLives: () => void
  getVictims: () => { leftVictim: Victim; rightVictim: Victim }
  handleLeftClick: () => void
  handleRightClick: () => void
}

export const WhoLivesQuestionScreen = ({
  sayVictimIntro,
  promptWhoLives,
  getVictims,
  handleLeftClick,
  handleRightClick,
}: WhoLivesQuestionScreenProps) => {
  const audioPlayedRef = useRef(false)
  const chosenRef = useRef(false)
  const mountedAtRef = useRef(Date.now())
  const containerRef = useRef<HTMLDivElement>(null)
  const leftImageRef = useRef<HTMLImageElement>(null)
  const rightImageRef = useRef<HTMLImageElement>(null)
  const [chosen, setChosen] = useState<Side | null>(null)
  const tv = isTvMode()

  useEffect(() => {
    if (!audioPlayedRef.current) {
      sayVictimIntro()
      promptWhoLives()
      audioPlayedRef.current = true
    }
    // Each round starts with focus next to the two choices instead of
    // being dropped on <body> by the scene swap. On TV the D-pad needs a
    // victim focused immediately, so the amber ring marks the fatal pick.
    if (isTvMode()) {
      leftImageRef.current?.focus()
    } else {
      containerRef.current?.focus()
    }
  }, [sayVictimIntro, promptWhoLives])

  // TV mode: Left/Right move the focus ring between victims, Enter (on
  // the focused image, via its existing role=button handler) confirms
  // the kill. Enter with no victim focused just restores focus — never
  // auto-kills.
  useEffect(() => {
    if (!isTvMode()) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        leftImageRef.current?.focus()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        rightImageRef.current?.focus()
      } else if (event.key === 'Enter') {
        const active = document.activeElement
        if (active !== leftImageRef.current && active !== rightImageRef.current) {
          event.preventDefault()
          leftImageRef.current?.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Guard against double-taps sentencing two victims at once, then mark
  // the choice on screen synchronously so the feedback is instant even
  // before the crash starts.
  const choose = (side: Side) => {
    if (chosenRef.current) return
    if (Date.now() - mountedAtRef.current < CHOICE_GRACE_MS) return
    chosenRef.current = true
    setChosen(side)
    if (side === 'left') {
      handleLeftClick()
    } else {
      handleRightClick()
    }
  }
  const chooseLeft = () => choose('left')
  const chooseRight = () => choose('right')

  const { leftVictim, rightVictim } = getVictims()
  return (
    <div
      className={`whoLivesQuestionScreenContainer${chosen ? ` chosen-${chosen}` : ''}`}
      ref={containerRef}
      tabIndex={-1}
    >
      <img
        className="leftVictimImage"
        ref={leftImageRef}
        src={leftVictim.image}
        alt={`Veer left to kill ${leftVictim.name}`}
        role="button"
        tabIndex={0}
        onClick={chooseLeft}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            chooseLeft()
          }
        }}
      />
      <img
        className="rightVictimImage"
        ref={rightImageRef}
        src={rightVictim.image}
        alt={`Veer right to kill ${rightVictim.name}`}
        role="button"
        tabIndex={0}
        onClick={chooseRight}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            chooseRight()
          }
        }}
      />
      {/* Name plates: the joke has to be readable without narration
          (Fire TV has none). Plates sit after the images so the TV focus
          hint can key off the focused image with a sibling selector; they
          are pointer-events:none so clicks still land on the images. */}
      <div className="victimPlate leftPlate" aria-hidden="true">
        <span className="plateSide">◀ LEFT</span>
        <span className="plateName">{leftVictim.name}</span>
        {tv && <span className="plateHint">{COPY.RUN_THEM_OVER}</span>}
      </div>
      <div className="victimPlate rightPlate" aria-hidden="true">
        <span className="plateSide">RIGHT ▶</span>
        <span className="plateName">{rightVictim.name}</span>
        {tv && <span className="plateHint">{COPY.RUN_THEM_OVER}</span>}
      </div>
      {chosen && (
        <div className={`splatStamp splat-${chosen}`} aria-hidden="true">
          SPLAT
        </div>
      )}
    </div>
  )
}
